'use client';

import React, { useState } from 'react';
import { ContactPageFormErrors, ContactPageFormValues, DefaultPageProps } from "@/lib/types/shared/page.types";
import { sendEmail } from '@/lib/api/EmailApi';
import { useFormik } from 'formik';
import { Typography, TextareaAutosize } from '@mui/material';
import { Input, InputLabel } from '@mui/material';
import ErrorMessage from "@/components/shared/forms/ErrorMessage";
import { useTranslations } from 'next-intl';
import { ButtonOne } from '@/components/shared';
import { Cursor } from '../shared/icons';
import { emailTemplateConfigContact } from '@/lib/constants/mailTemplates';

const ValidateForm = (values: ContactPageFormValues) => {
    const errors: ContactPageFormErrors = {};

    // Name Validation
    if (!values.name) {
        errors.name = 'required';
    } else if (values.name.length < 2) {
        errors.name = 'name_must_be_at_least_2_characters';
    } else {
        if (errors.name) delete errors.name;
    }

    // Phone Validation
    if (!values.phone) {
        errors.phone = 'required';
    } else if (values.phone.length < 10) {
        errors.phone = 'phone_must_be_at_least_10_characters';
    } else {
        if (errors.phone) delete errors.phone;
    }

    // Email Validation
    if (!values.email) {
        errors.email = 'required';
    } else if (!/^[\w%+.-]+@[\d.a-z-]+\.[a-z]{2,4}$/i.test(values.email)) {
        errors.email = 'invalid_email_address';
    } else {
        if (errors.email) delete errors.email;
    }

    // Message Validation
    if (!values.message) {
        errors.message = 'required';
    } else if (values.message.length < 10) {
        errors.message = 'message_must_be_at_least_10_characters';
    } else {
        if (errors.message) delete errors.message;
    }

    return errors;
};

type Props = {
    className?: string;
} & DefaultPageProps;

const Form = ({ className = '', params }: Props) => {
    const t = useTranslations('pages.contact');
    const t_global = useTranslations();

    const [formSubmitting, setFormSubmitting] = useState(false);
    const [formSubmitted, setFormSubmitted] = useState(false);

    const formik = useFormik({
        initialValues: {
            name: '',
            phone: '',
            email: '',
            message: '',
        },
          
        validate: ValidateForm,
        
        onSubmit: async (values) => {
            setFormSubmitting(true);

            console.log(values);

            const emailTemplateConfig = emailTemplateConfigContact(values.name, values.phone, values.email, values.message);
            sendEmail(emailTemplateConfig.subject, emailTemplateConfig.html).then((response: any) => {
                setFormSubmitting(false);
                setFormSubmitted(true);
            }).catch((error: any) => {
                console.error('error', error);
                setFormSubmitting(false);
            });
        }
    });

    type Event = {
        target: {
            name: string;
            value: string;
        };
    };

    const handleFormChange = (e: Event) => {
        formik.handleChange(e);
    }

    const handleFormBlur = (e: Event) => {
        formik.handleBlur(e);
    }

    const errors = formik.errors;
    const touched = formik.touched;
    const values = formik.values;

    return (
        <form className={className} onSubmit={formik.handleSubmit}>
            {formSubmitted ? (
                <>
                    <Typography variant='h4' component='h2' className='text-primary-gray-950 font-bold capitalize'>
                        {t('form.thanks.title')}
                    </Typography>

                    <Typography variant='body1' className='text-primary-gray-950'>
                        {t('form.thanks.text')}
                    </Typography>
                </>
            ) : (
                <div className='flex flex-col gap-4'>
                    <div className="field flex-grow">
                        <InputLabel id="name-label" htmlFor='name' className='mb-2 cursor-pointer'>
                            {t("form.labels.0")}
                        </InputLabel>

                        <div className="transition rounded-xl bg-white border border-primary-gray-300">
                            <Input id='name' className='w-full rounded-xl text-primary-gray-950 px-4 py-1' type="text" name="name" placeholder={t('form.placeholders.0')} disableUnderline={true} onChange={handleFormChange} onBlur={handleFormBlur} value={values.name} required />
                        </div>
                        <ErrorMessage>{errors.name && touched.name && t_global(errors.name)}</ErrorMessage>
                    </div>
                    <div className="field flex-grow">
                        <InputLabel id="phone-label" htmlFor='phone' className='mb-2 cursor-pointer'>
                            {t("form.labels.1")}
                        </InputLabel>

                        <div className="transition rounded-xl bg-white border border-primary-gray-300">
                            <Input id='phone' className='w-full rounded-xl text-primary-gray-950 px-4 py-1' type="text" name="phone" placeholder={t('form.placeholders.1')} disableUnderline={true} onChange={handleFormChange} onBlur={handleFormBlur} value={values.phone} required />
                        </div>
                        <ErrorMessage>{errors.phone && touched.phone && t_global(errors.phone)}</ErrorMessage>
                    </div>
                    <div className="field flex-grow">
                        <InputLabel id="email-label" htmlFor='email' className='mb-2 cursor-pointer'>
                            {t("form.labels.2")}
                        </InputLabel>

                        <div className="transition rounded-xl bg-white border border-primary-gray-300">
                            <Input id='email' className='w-full rounded-xl text-primary-gray-950 px-4 py-1' type="email" name="email" placeholder={t('form.placeholders.2')} disableUnderline={true} onChange={handleFormChange} onBlur={handleFormBlur} value={values.email} required />
                        </div>
                        <ErrorMessage>{errors.email && touched.email && t_global(errors.email)}</ErrorMessage>

                        <Typography variant='body2' className='text-primary-gray-950'>{t("form.email_note")}</Typography>
                    </div>
                    <div className="field flex-grow">
                        <InputLabel id="message-label" htmlFor='message' className='mb-2 cursor-pointer'>
                            {t("form.labels.3")}
                        </InputLabel>

                        <div className="transition rounded-xl bg-white border border-primary-gray-300">
                            <TextareaAutosize className='w-full rounded-xl text-primary-gray-950 px-4 py-1 border-none outline-none ring-0 block bg-transparent' name="message" placeholder={t('form.placeholders.3')} onChange={handleFormChange} onBlur={handleFormBlur} minRows={3} value={values.message} required  />
                        </div>
                        <ErrorMessage>{errors.message && touched.message && t_global(errors.message)}</ErrorMessage>
                    </div>

                    <ButtonOne paddingX='extra-small' customIcon={
                        <div className='bg-primary-yellow-t rounded-lg p-2'>
                            <Cursor color='fill-primary-yellow-400' />
                        </div>
                    } iconPos='left' variant='green' type='button' text={t('form.button')} className='ml-auto mr-0 skip' />
                </div>
            )}
        </form>
    );
}

export default Form;